/**
 * RPG entity model — the Mini-RPG domain (ТЗ №5, под-фаза D3).
 *
 * Mirrors the backend D2 serializers (``apps.rpg.serializers``). Every value is
 * server-authoritative — stats, rewards, prices, drop chances, the conversion rate and
 * all balances come from the API; the client only displays them and names the action
 * (which mob / which weapon / which recipe / how much mora). The frontend never computes
 * or re-checks economic values.
 */

/** A currency that can be transferred between players (matches ``TRANSFERABLE_CURRENCIES``). */
export type TransferCurrency = "mora" | "primogem";

/** Hero stats the player can raise for mora (matches ``UPGRADE_STATS``). */
export type UpgradeStat = "hp" | "attack";

// --------------------------------------------------------------------------- #
// Profile
// --------------------------------------------------------------------------- #
/** The currently equipped weapon, embedded in the profile (``EquippedWeaponSerializer``). */
export interface EquippedWeapon {
  readonly id: number;
  readonly name: string;
  readonly icon_url: string;
  readonly level: number;
  readonly damage: number;
}

/** Hero profile + balances + derived damage (``ProfileStateSerializer``). */
export interface RpgProfile {
  readonly rank: number;
  readonly level: number;
  readonly xp: number;
  readonly xp_next: number;
  readonly hp: number;
  readonly max_hp: number;
  readonly battle_quota: {
    readonly used: number;
    readonly limit: number;
    readonly remaining: number;
    readonly resets_at: string;
  };
  readonly battle_hp_cost: number;
  readonly hp_regen_per_minute: number;
  readonly seconds_to_next_hp: number;
  readonly attack: number;
  readonly effective_damage: number;
  readonly equipped_weapon: EquippedWeapon | null;
  readonly mora: number;
  readonly primogems: number;
  readonly star_glitter: number;
  readonly battles_count: number;
  readonly wins: number;
  readonly boss_wins: number;
  readonly win_rate: number;
  readonly mora_per_primogem: number;
  readonly upgrade_hp_cost: number;
  readonly upgrade_hp_gain: number;
  readonly upgrade_attack_cost: number;
  readonly upgrade_attack_gain: number;
}

// --------------------------------------------------------------------------- #
// Mobs
// --------------------------------------------------------------------------- #
/** A battleable mob with per-hero availability (``MobSerializer``). */
export interface RpgMob {
  readonly id: number;
  readonly name: string;
  readonly icon_url: string;
  readonly level: number;
  readonly hp: number;
  readonly attack: number;
  readonly win_chance: number;
  readonly reward_mora: number;
  readonly reward_xp: number;
  readonly required_rank: number;
  readonly daily_limit: number;
  readonly cooldown_seconds: number;
  readonly is_boss: boolean;
  readonly available: boolean;
  readonly lock_reason: string;
  readonly cooldown_remaining: number;
  readonly fought_today: number;
}

// --------------------------------------------------------------------------- #
// Inventory + weapons
// --------------------------------------------------------------------------- #
/** An owned inventory item with quantity (``InventorySerializer``). */
export interface RpgInventoryItem {
  readonly item_id: number;
  readonly name: string;
  readonly icon_url: string;
  readonly description: string;
  readonly rarity: string;
  readonly kind: string;
  readonly qty: number;
}

/** A material required to upgrade a weapon (``WeaponMaterialSerializer``). */
export interface RpgWeaponMaterial {
  readonly item_id: number;
  readonly name: string;
  readonly qty: number;
}

/** A weapon from the catalog with owned/equipped state (``WeaponSerializer``). */
export interface RpgWeapon {
  readonly id: number;
  readonly name: string;
  readonly icon_url: string;
  readonly rarity: string;
  readonly base_damage: number;
  readonly owned: boolean;
  readonly owned_level: number;
  readonly effective_damage: number;
  readonly equipped: boolean;
  readonly upgrade_cost_mora: number;
  readonly upgrade_materials: readonly RpgWeaponMaterial[];
}

// --------------------------------------------------------------------------- #
// Alchemy + shop
// --------------------------------------------------------------------------- #
/** A material input for a recipe (``RecipeInputSerializer``). */
export interface RpgRecipeInput {
  readonly item_id: number;
  readonly name: string;
  readonly qty: number;
}

/** An alchemy recipe (``RecipeSerializer``). */
export interface RpgRecipe {
  readonly id: number;
  readonly name: string;
  readonly result_item_id: number;
  readonly result_name: string;
  readonly result_icon_url: string;
  readonly result_qty: number;
  readonly cost_mora: number;
  readonly required_rank: number;
  readonly inputs: readonly RpgRecipeInput[];
}

/** An in-game shop listing (``ShopItemSerializer``). */
export interface RpgShopItem {
  readonly id: number;
  readonly kind: string;
  readonly name: string;
  readonly icon_url: string;
  readonly price_mora: number;
  readonly purchase_limit: number;
  readonly purchased: number;
  readonly sold_out: boolean;
}

// --------------------------------------------------------------------------- #
// Ratings
// --------------------------------------------------------------------------- #
/** A single leaderboard row (``RatingEntrySerializer``). */
export interface RpgRatingEntry {
  readonly user_id: number;
  readonly name: string;
  readonly value: number;
}

/** All leaderboards (``RatingsSerializer``). */
export interface RpgRatings {
  readonly by_rank: readonly RpgRatingEntry[];
  readonly by_mora: readonly RpgRatingEntry[];
  readonly by_primogems: readonly RpgRatingEntry[];
  readonly by_wins: readonly RpgRatingEntry[];
  readonly by_boss_wins: readonly RpgRatingEntry[];
}

// --------------------------------------------------------------------------- #
// Action results
// --------------------------------------------------------------------------- #
/** A material drop from a battle (``DropSerializer``). */
export interface RpgDrop {
  readonly item_id: number;
  readonly name: string;
  readonly qty: number;
}

/** Outcome of a single battle plus the refreshed profile (``BattleResultSerializer``). */
export interface RpgBattleResult {
  readonly mob_id: number;
  readonly won: boolean;
  readonly mora_reward: number;
  readonly xp_reward: number;
  readonly drops: readonly RpgDrop[];
  readonly leveled_up: boolean;
  readonly new_level: number;
  readonly new_rank: number;
  readonly profile: RpgProfile;
}

/** Result of a mora→primogem conversion (``ConvertResultSerializer``). */
export interface RpgConvertResult {
  readonly mora_spent: number;
  readonly primogems_gained: number;
  readonly mora: number;
  readonly primogems: number;
}

/** Result of a player-to-player transfer (``TransferResultSerializer``). */
export interface RpgTransferResult {
  readonly recipient_id: number;
  readonly recipient_label: string;
  readonly currency: TransferCurrency;
  readonly amount: number;
  readonly sender_balance: number;
}

// --------------------------------------------------------------------------- #
// Inputs
// --------------------------------------------------------------------------- #
/** Body for ``POST /api/rpg/transfer/`` (``TransferInputSerializer``). */
export interface RpgTransferInput {
  readonly to: string;
  readonly currency: TransferCurrency;
  readonly amount: number;
}

// --------------------------------------------------------------------------- #
// Presentation helpers (display-only — never derive economic values)
// --------------------------------------------------------------------------- #
/** RU label for a transfer currency. */
export const CURRENCY_LABELS: Readonly<Record<TransferCurrency, string>> = {
  mora: "Мора",
  primogem: "Примогемы",
};

/** RU label for a hero stat upgrade. */
export const STAT_LABELS: Readonly<Record<UpgradeStat, string>> = {
  hp: "Здоровье",
  attack: "Урон",
};

/** XP progress as a 0..100 percentage (presentation only). */
export function xpPercent(state: Pick<RpgProfile, "xp" | "xp_next">): number {
  if (state.xp_next <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((state.xp / state.xp_next) * 100));
}

/** HP progress as a 0..100 percentage (presentation only). */
export function hpPercent(state: Pick<RpgProfile, "hp" | "max_hp">): number {
  if (state.max_hp <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round((state.hp / state.max_hp) * 100)));
}

/**
 * Whether the hero currently has enough HP to start a battle (server-authoritative rule
 * mirrored for a pre-emptive block — the backend re-checks and raises ``InsufficientHp``).
 */
export function canBattle(state: Pick<RpgProfile, "hp" | "battle_hp_cost">): boolean {
  return state.hp >= state.battle_hp_cost;
}

/** RU hint shown when a battle is blocked for lack of HP (mentions the regen rate). */
export function insufficientHpLabel(
  state: Pick<RpgProfile, "hp_regen_per_minute">,
): string {
  return state.hp_regen_per_minute > 0
    ? `Недостаточно HP — восстановление ${state.hp_regen_per_minute}/мин`
    : "Недостаточно HP";
}

/** A win chance (0..1) rendered as a whole-percent string. */
export function winChancePercent(chance: number): number {
  return Math.round(Math.max(0, Math.min(1, chance)) * 100);
}

/** Compact ``m:ss`` (or ``h:mm:ss``) label for a cooldown in seconds. */
export function formatCooldown(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Reason a mob cannot be fought, ready to display (presentation only).
 *
 * Returns ``null`` for an available mob (or one with no server-supplied reason). An
 * active cooldown takes precedence and is shown as a countdown; otherwise the server's
 * localized ``lock_reason`` (rank too low / daily limit reached) is used verbatim.
 */
export function mobLockLabel(
  mob: Pick<RpgMob, "available" | "lock_reason" | "cooldown_remaining">,
): string | null {
  if (mob.available || mob.lock_reason.length === 0) {
    return null;
  }
  if (mob.cooldown_remaining > 0) {
    return `Перезарядка: ${formatCooldown(mob.cooldown_remaining)}`;
  }
  return mob.lock_reason;
}
